import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { auth, db } from "../firebase";
import { useSchool } from "../hooks/useSchool";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const AccountPage = () => {
  const schoolId = useSchool();
  const navigate = useNavigate();

  const [role, setRole] = useState(null);
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // 🔒 ADMIN PROTECTION
  useEffect(() => {
    const checkRole = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const userDoc = await getDoc(doc(db, "users", uid));

      if (!userDoc.exists()) {
        navigate("/");
        return;
      }

      const userData = userDoc.data();
      setRole(userData.role);

      if (userData.role !== "admin") {
        navigate("/");
      }
    };

    checkRole();
  }, [navigate]);

  // Optional loading state
  if (role === null) {
    return <p style={{ textAlign: "center", marginTop: "2rem" }}>Loading...</p>;
  }

  const handleManageBilling = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();

      const res = await fetch(
        "https://us-central1-pbis-app-72ba5.cloudfunctions.net/createCustomerPortalSession",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ schoolId }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      window.location.href = data.url;
    } catch (err) {
      console.error(err);
      alert("Unable to open billing portal yet.");
    }
  };

  const handleSubmitRequest = async () => {
    if (!message.trim()) return;
  
    try {
      await addDoc(
        collection(db, "schools", schoolId, "featureRequests"),
        {
          message,
          createdAt: serverTimestamp(),
          submittedBy: auth.currentUser?.email || "unknown",
        }
      );
  
      setSubmitted(true);
      setMessage("");
    } catch (err) {
      console.error(err);
      alert("Failed to submit request.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Account & Billing | PBIS Portal</title>
        <meta
          name="description"
          content="Manage your PBIS Portal subscription, billing, and account support."
        />
        <link
          rel="canonical"
          href={`https://pbisportal.com/${schoolId}/account`}
        />
      </Helmet>

      <div className="dashboard-container">
        <h2 className="section-title">⚙️ Account & Billing</h2>

        {/* Subscription */}
        <div className="section-card">
          <h3>💳 Subscription</h3>
          <p>
            You can manage your billing, payment method, and subscription here.
          </p>

          <button
            onClick={handleManageBilling}
            style={{
              backgroundColor: "#2563eb",
              color: "white",
              padding: "0.6rem 1.2rem",
              borderRadius: "6px",
              border: "none",
              marginTop: "0.5rem",
            }}
          >
            Manage Billing
          </button>

          <p
            style={{
              marginTop: "0.75rem",
              color: "#6b7280",
              fontSize: "0.9rem",
            }}
          >
            Cancel anytime. No contracts.
          </p>
        </div>

        {/* Help */}
        <div className="section-card">
          <h3>🆘 Need Help?</h3>
          <p>Questions about setup or getting the most out of PBIS Portal?</p>

          <a
            href="mailto:rmelhus@nlcschool.org"
            style={{ color: "#2563eb", fontWeight: "500" }}
          >
            Contact Support
          </a>
        </div>

        {/* Feature Request */}
        <div className="section-card">
          <h3>💡 Feature Request</h3>
          <p>Have an idea that would make PBIS Portal better?</p>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Share your idea..."
            style={{
              width: "100%",
              padding: "0.5rem",
              marginTop: "0.5rem",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              minHeight: "80px",
            }}
          />

          <button
            onClick={handleSubmitRequest}
            style={{
              backgroundColor: "#22c55e",
              color: "white",
              padding: "0.5rem 1rem",
              borderRadius: "6px",
              border: "none",
              marginTop: "0.5rem",
            }}
          >
            Submit
          </button>

          {submitted && (
            <p style={{ color: "green", marginTop: "0.5rem" }}>
              ✅ Thanks! We appreciate the feedback.
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default AccountPage;
